/**
 * Route path constants for the InDeep sponsor PWA.
 * All sponsor-gated routes require active sponsor_access.
 */
export const RoutePaths = {
  // Public routes
  HOME: '/',
  REGISTER: '/register',
  LOGIN: '/login',
  MOBILE_LOGIN: '/login/mobile',
  LOGIN_VERIFY: '/login/verify',
  VERIFY: '/verify',
  ONBOARDING: '/onboarding',
  PAYWALL: '/paywall',

  // Sponsor-gated routes (require sponsor_access)
  DISCOVER: '/discover',
  CARD_DETAIL: '/card/:id',
  INTRO_REQUEST: '/intro-request',

  // Friend-gated routes (G1 recipient lifecycle)
  FRIEND_INBOX: '/friend/inbox',
  FRIEND_INBOX_DETAIL: '/friend/inbox/:id',

  // Sponsor profile routes (require sponsor registration kind)
  SPONSOR_PROFILE: '/sponsor-profile',

  // Feed routes
  FEED: '/feed',
  FEED_COMPOSE: '/feed/compose',
  FEED_COMMENTS: '/feed/:postId/comments',

  // Account routes
  ACCOUNT: '/account',
  PROFILE_COMPLETION: '/profile-completion',
  PROFILE_QUESTIONNAIRE: '/profile-questionnaire',
  PRIVACY: '/privacy',
  REPORT: '/report',
  CONTACT: '/contact',

  // Chat routes
  CHAT: '/chat',
  CHAT_CHANNEL: '/chat/:channelId',
  CHAT_OPEN_PERSONAL: '/chat/open/:targetProfileId',

  // Admin routes
  ADMIN: '/admin',
} as const;

// Route metadata for guards and navigation
export interface RouteMeta {
  path: string;
  label: string;
  isPublic: boolean;
  requiresSponsorAccess: boolean;
  /** Admin-only route requiring explicit admin role check */
  isAdmin?: boolean;
}

export const ROUTES: RouteMeta[] = [
  { path: RoutePaths.HOME, label: 'Home', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.REGISTER, label: 'Register', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.LOGIN, label: 'Login', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.MOBILE_LOGIN, label: 'Mobile Login', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.LOGIN_VERIFY, label: 'Login Verify', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.VERIFY, label: 'Verify', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.ONBOARDING, label: 'Onboarding', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.PAYWALL, label: 'Access', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.DISCOVER, label: 'Discover', isPublic: false, requiresSponsorAccess: true },
  { path: RoutePaths.CARD_DETAIL, label: 'Card Detail', isPublic: false, requiresSponsorAccess: true },
  { path: RoutePaths.INTRO_REQUEST, label: 'Intro Request', isPublic: false, requiresSponsorAccess: true },
  { path: RoutePaths.ACCOUNT, label: 'Account', isPublic: false, requiresSponsorAccess: false },
  { path: RoutePaths.PRIVACY, label: 'Privacy', isPublic: false, requiresSponsorAccess: false },
  { path: RoutePaths.REPORT, label: 'Report', isPublic: false, requiresSponsorAccess: false },
  { path: RoutePaths.CONTACT, label: 'Contact', isPublic: true, requiresSponsorAccess: false },
  { path: RoutePaths.CHAT, label: 'Chat', isPublic: false, requiresSponsorAccess: false },
  { path: RoutePaths.CHAT_CHANNEL, label: 'Chat Channel', isPublic: false, requiresSponsorAccess: false },
  // Admin routes - require explicit admin role check, NOT sponsor-gated
  { path: RoutePaths.ADMIN, label: 'Admin', isPublic: false, requiresSponsorAccess: false, isAdmin: true },
];

// Sponsor-gated routes that require sponsor_access
export const SPONSOR_GATED_ROUTES = ROUTES.filter(r => r.requiresSponsorAccess).map(r => r.path);

// Public routes (no auth required)
export const PUBLIC_ROUTES = ROUTES.filter(r => r.isPublic).map(r => r.path);

// Admin routes - require explicit admin role check
export const ADMIN_ROUTES = ROUTES.filter(r => r.isAdmin).map(r => r.path);

// =============================================================================
// Route builders
// =============================================================================

/**
 * Allowed characters for post IDs in URLs.
 *
 * This is the URL-safe subset that survives encoding unchanged and matches the
 * shape of our canonical post IDs (`post-anna-001`, `abc-123`, etc.).
 *
 * Conservative allowlist: lowercase letters, digits, dash, underscore. Anything
 * else is rejected so we never interpolate untrusted text into a route path.
 */
const SAFE_POST_ID = /^[a-zA-Z0-9_-]+$/;

/**
 * Build the canonical path for a post's comments screen.
 *
 * @throws RangeError when `postId` contains characters outside the safe
 *         allowlist. Callers must NOT bypass this check by string concat.
 */
export function buildFeedCommentsPath(postId: string): string {
  if (typeof postId !== 'string' || postId.length === 0 || postId.length > 128) {
    throw new RangeError('buildFeedCommentsPath: postId must be a non-empty string ≤128 chars');
  }
  if (!SAFE_POST_ID.test(postId)) {
    throw new RangeError('buildFeedCommentsPath: postId contains unsafe characters');
  }
  return `/feed/${encodeURIComponent(postId)}/comments`;
}

/**
 * Backend chat IDs are UUIDs. The fixture adapter additionally uses bounded
 * `channel-*` aliases so route proof does not pretend fixture data is backend
 * data. Both forms reject separators, traversal, query, and fragment syntax.
 */
const SAFE_CHANNEL_ID = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|channel-[a-z0-9]+(?:-[a-z0-9]+)*)$/;

export function buildChatChannelPath(channelId: string): string {
  if (typeof channelId !== 'string' || channelId.length === 0 || channelId.length > 128) {
    throw new RangeError('buildChatChannelPath: channelId must be a non-empty string ≤128 chars');
  }
  if (!SAFE_CHANNEL_ID.test(channelId)) {
    throw new RangeError('buildChatChannelPath: channelId contains unsafe characters');
  }
  return `/chat/${encodeURIComponent(channelId)}`;
}
